import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import axios from 'axios';
import Main from '../../../component/Main';
import Loading from '../../../component/common/loading/Loading';
import NewCommonSelect from '../../../component/common/selectBox/NewCommonSelect';
import generateFidelityList from './generate_fidelity_list';
import FidelityExcelDownload from './FidelityExcelDownload';
import useModalStore from '../../../zustand/store/useModalStore';

import '../../../css/data-grid.css';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import { Search16w } from '../../../images/icons/icons_component';

// 엑셀 다운로드 컬럼 너비 설정값
export const SEND_STAT_COL_WIDTHS = [7, 17, 17, 30, 30, 30, 30, 30, 30];

/**
 * @월별전송내역그리드
 * 월별전송내역 메뉴 / 월별 모니터링 메뉴 공용 그리드
 * 조회 API(fidelity_list.do, fidelity_data.do)를 컴포넌트 내부에서 처리
    <SendStatMonthlyGrid
        ref={sendGridRef}               // ref.search() 로 조회 실행
        searchParams={searchParams}     // 검색 조건(부모가 소유)
        showTitle={true}                // title 표출 여부(단독 메뉴는 main title 이 있으므로 false)
        onSearched={(result) => {}}     // 조회 완료 시 { totalItems, searchedTime, emogdesc, yyyymm } 전달
                                        // emogdesc / yyyymm 은 조회 시점 기준 값(엑셀 파일명 고정용)
    />
 * **/
export const SendStatMonthlyGrid = forwardRef(({ searchParams, showTitle = true, onSearched }, ref) => {

    const [isSearched, setIsSearched] = useState(false);
    const [searchedTime, setSearchedTime] = useState();
    const [fidelityData, setFidelityData] = useState([{}]);
    const [totalItems, setTotalItems] = useState(0);

    const [bodylist, setBodyList] = useState([]);
    const head = ['항목', '정상저장률(전국)(%)', '정상저장률(%)', '미상저장률(%)', '미입력률(%)', '재검토율(%)', '오류율(%)'];
    const except_body = ['PTMIHIBP', 'PTMILOBP', 'PTMIPULS', 'PTMIBRTH', 'PTMIBDHT', 'PTMIVOXS'];

    const bodyData = async () => {
        try {
            const response = await axios.get('/ptmiapi/fidelity_list.do',
                { params: { listType: 'ALL' } }
            );

            setBodyList(response.data.fidelitylist);

        } catch (e) {
            console.error('body 내용 로딩 중 오류 발생', e);
        }
    }

    const getfidelityData = async () => {
        try {
            const response = await axios.get('/ptmiapi/fidelity_data.do',
                { params: searchParams }
            );

            // 조회 후 기관구분을 바꿔도 항목 조회에 실패하지 않도록 항상 전체 항목으로 생성(표출 대상은 렌더링 시점의 searchType 으로 필터링)
            setFidelityData(generateFidelityList(bodylist, response.data, '01'));
            setIsSearched(true);

            const cnt = response.data?.statInfo.TOTAL_CNT1 ?? 0;
            setTotalItems(cnt);

            const now = new Date();

            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');  // 0-based
            const date = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');

            const formatted = `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`;

            setSearchedTime(formatted);

            onSearched?.({
                totalItems: cnt,
                searchedTime: formatted,
                emogdesc: response.data?.statInfo.EMOGDESC,
                yyyymm: searchParams.fromindt.substring(0, 6)
            });

        } catch (e) {
            console.error('월별 전송내역 내용 로딩 중 오류 발생', e);
        }
    }

    useEffect(() => {
        bodyData();
    }, []);

    // 부모(단독 메뉴 / 모니터링) 조회 handle
    useImperativeHandle(ref, () => ({
        search: getfidelityData
    }));

    const onClickPopup = (e) => {
        const cls = e.currentTarget.dataset.cls;
        const clsdesc = e.target.dataset.clsdesc;
        const type = e.target.dataset.type;
        const deno = e.target.dataset.deno;
        const sendData = {
            cls: cls,
            clsdesc: clsdesc,
            type: type,
            deno: deno,
            flag: 'ptmi'
        }

        let popup = null;

        const totalObj = Object.assign({}, searchParams, sendData)

        if (isSearched && type && deno) {
            localStorage.setItem('sendData', JSON.stringify(totalObj));
            popup = window.open('/nedis/fidelity_2016/send_stat_monthly_popup.do', '팝업', 'scrollbars=1, resizable=0, width=950, height=550, top=30, left=30');
        }
    }

    // Dynamic Style Hook
    const { fontColor, hoverLightFontColor, borderColorGray } = useDynamicStyle();

    return (
        <>
            {showTitle &&
                <table className='w-full mb-3'>
                    <colgroup>
                        <col width='4%' />
                        <col width='9%' />
                        <col width='9%' />
                        <col width='13%' />
                        <col width='13%' />
                        <col width='13%' />
                        <col width='13%' />
                        <col width='13%' />
                        <col width='13%' />
                    </colgroup>
                    <tbody>
                        <tr className='h-4'>
                            <td colSpan={4} className='table_title'>
                                <h5 className='sub_title m-0'>▶ NEDIS 월별 전송내역 결과 (총&nbsp;<strong className={fontColor}>{totalItems}</strong>건)</h5>
                            </td>
                            <td colSpan={5} className='search_time'>
                                <p className='w-full co_red pr-2 text-right'>{isSearched ? '※ ' + searchedTime + ' 기준으로 조회 된 데이터 입니다.' : ''}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            }
            <div className='data_grid_container'>
                <hr className={`border-t-2 mr-1-1 ${borderColorGray}`} />
                <div className={`data_list_table ${isSearched ? 'grid-animation' : ''}`}>
                    <table>
                        <colgroup>
                            <col width='4%' />
                            <col width='9%' />
                            <col width='9%' />
                            <col width='13%' />
                            <col width='13%' />
                            <col width='13%' />
                            <col width='13%' />
                            <col width='13%' />
                            <col width='13%' />
                        </colgroup>
                        <thead>
                            <tr className='table_header'>
                                {head.map((content, index) => (
                                    <th colSpan={index === 0 ? 3 : 1} key={index} className='table_header'>
                                        {content}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {bodylist.filter(content => !(searchParams.searchType === '02' && content.FIDELITY_TYPE === '02')).map((content, index) => (
                                <tr key={index + 1} onClick={onClickPopup} className={searchParams.searchType === '02' && content.FIDELITY_TYPE === '02' ? 'hidden' : content.FIDELITY_TYPE === '02' ? '' : ' colored'} data-cls={content.SEQ < 10 ? '0' + (content.SEQ) : content.SEQ}>
                                    <td>{index + 1}</td>
                                    {content.FIDELITY_CODE === 'PTMIHIBP' ? <td className='middle' rowSpan='6'><span>내원 시<br /> 활력징후</span></td> : ''}
                                    <td colSpan={except_body.includes(content.FIDELITY_CODE) ? '1' : '2'}>{content.FIDELITY_NAME}</td>
                                    <td id={content.FIDELITY_CODE}>{isSearched ? fidelityData?.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].NATIONAL_NORMAL : '0.00'}</td>
                                    <td id={content.FIDELITY_CODE}>{isSearched ? fidelityData?.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].ORG_NORMAL : '0.00'}</td>
                                    <td data-deno={isSearched ? fidelityData?.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].TOTAL_CNT : ''} data-clsdesc={content.FIDELITY_NAME} data-type='unknown' id={content.FIDELITY_CODE} className={isSearched ? 'underline-pointer ' + hoverLightFontColor : ''}>{isSearched ? fidelityData && fidelityData.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].ORG_UNKNOWN : '0.00'}</td>
                                    <td data-deno={isSearched ? fidelityData?.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].TOTAL_CNT : ''} data-clsdesc={content.FIDELITY_NAME} data-type='blank' id={content.FIDELITY_CODE} className={isSearched ? 'underline-pointer ' + hoverLightFontColor : ''}>{isSearched ? fidelityData && fidelityData.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].ORG_BLANK : '0.00'}</td>
                                    <td data-deno={isSearched ? fidelityData?.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].TOTAL_CNT : ''} data-clsdesc={content.FIDELITY_NAME} data-type='review' id={content.FIDELITY_CODE} className={isSearched ? 'underline-pointer ' + hoverLightFontColor : ''}>{isSearched ? fidelityData && fidelityData.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].ORG_REVIEW : '0.00'}</td>
                                    <td data-deno={isSearched ? fidelityData?.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].TOTAL_CNT : ''} data-clsdesc={content.FIDELITY_NAME} data-type='error' id={content.FIDELITY_CODE} className={isSearched ? 'underline-pointer ' + hoverLightFontColor : ''}>{isSearched ? fidelityData && fidelityData.filter(data => data.FIDELITY_ID === content.FIDELITY_CODE)[0].ORG_ERROR : '0.00'}</td>
                                </tr>
                            ))
                            }
                        </tbody>
                    </table>
                </div>
                <hr className='mr-1-1 co_border_gray' />
                {/* 범례 - 엑셀전용 table */}
                <table>
                    <colgroup>
                        {Array.from({ length: 9 }).map((_, idx) => (
                            <col key={idx} width={`${100 / 9}%`} />
                        ))}
                    </colgroup>
                    <tbody>
                        <tr>
                            <td colSpan={3} className='border-none py-0'>
                                <div className='mt-2'>
                                    <div className='flex items-center'>
                                        <span className='st_row bold'>■ </span>
                                        <p className='ml-1'>&nbsp;센터급, 기관급 공통항목</p>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={9} className='border-none pt-0'>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    )
});

// 모니터링에는 사용하지 않고 단독 메뉴에서만 표출하는 비고 (엑셀 다운로드에 그대로 포함되도록 table 로 구성하고 tableRef 안에 배치)
const SEND_STAT_DESC_TEXTS = [
    '· 정상저장률 : 지침서코드와 동일한 경우',
    '· 미상저장률 : 자료가 없는 경우 \'default\' 전송을 의미',
    '· 미입력률 : 전송값이 전혀 없는 경우 (null 로 전송된 경우, 즉 \'default\' 전송도 없는 경우)',
    '· 재검토율 : 오류내역은 아니지만 확인이 필요한 경우를 의미',
    '· 오류율 : 입력지침서에 지정된 코드와 정상 범위 이외의 값을 전송 한 경우를 의미',
    '· 퇴원정보의 미입력률에는 재원중인 환자도 포함될 수 있음.'
];

export const SendStatMonthlyDesc = () => (
    <table>
        <colgroup>
            {Array.from({ length: 9 }).map((_, idx) => (
                <col key={idx} width={`${100 / 9}%`} />
            ))}
        </colgroup>
        <tbody>
            {SEND_STAT_DESC_TEXTS.map((content, index) => (
                <tr key={index}>
                    {/* excel_black_text : 엑셀전용 클래스 */}
                    <td colSpan={9} className='border-none pt-0 excel_black_text'>
                        <p className='ml-2'>{content}</p>
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const Send_stat_monthly_2016 = () => {

    const today = new Date();
    const currentYear = today.getFullYear();
    let currentMonth = today.getMonth() + 1;
    if (currentMonth < 10) {
        currentMonth = '0' + currentMonth;
    }

    const [searchParams, setSearchParams] = useState({
        searchYear: currentYear,
        searchMonth: currentMonth,
        searchEmog: localStorage.getItem('userAuth') === 'J003' ? localStorage.getItem('useremcd') : '',
        searchGrdc: localStorage.getItem('userAuth') === 'J003' ? localStorage.getItem('emoggrdc2') : '',
        searchLoca: localStorage.getItem('userAuth') === 'J003' ? localStorage.getItem('emogloca') : '',
        searchType: '01',
        fromindt: currentYear + '' + currentMonth + '01',
        toindt: currentYear + '' + currentMonth + '31',
    });

    const [isSearched, setIsSearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // 그리드 조회 결과 요약(건수 / 조회 기준 시각)
    const [totalItems, setTotalItems] = useState(0);
    const [searchedTime, setSearchedTime] = useState();
    
    // 엑셀 파일명을 '조회 시점' 기준으로 고정하기 위한 state
    const [excelInfo, setExcelInfo] = useState({ emogdesc: '', yyyymm: '' });

    // 그리드 조회 실행용 ref / 엑셀 다운로드 대상 ref
    const gridRef = useRef(null);
    const tableRef = useRef(null);

    // toast modal open function
    const showNotiModal = useModalStore(state => state.showNotiModal);

    const search = async () => {
        if (searchParams.searchEmog === '') {
            showNotiModal('먼저 해당 응급의료기관을 선택하십시오.');
        } else {
            setIsLoading(true);
            await gridRef.current?.search();
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newParams = {
            ...searchParams,
            [name]: value
        };
        if (name === 'searchYear' || name === 'searchMonth') {
            const year = name === 'searchYear' ? value : searchParams.searchYear;
            const month = name === 'searchMonth' ? value : searchParams.searchMonth;
            newParams.fromindt = `${year}${month}01`;
            newParams.toindt = `${year}${month}31`;
        }
        setSearchParams(newParams);
    };

    // Dynamic Style Hook
    const { fontColor, primaryBackColor } = useDynamicStyle();

    return (
        <Main>
            <div className='com-search'>
                <table>
                    <colgroup>
                        <col width='10%' />
                        <col width='20%' />
                        <col width='10%' />
                        <col width='20%' />
                        <col width='10%' />
                        <col width='20%' />
                    </colgroup>
                    <tbody>
                        <tr>
                            <NewCommonSelect
                                hospitalTF={true}
                                searchParams={searchParams}
                                handleInputChange={handleInputChange}
                                exValidation={true}
                            />
                        </tr>
                        <tr>
                            <th>내원기간</th>
                            <td>
                                <select id='searchYear'
                                    name='searchYear'
                                    value={searchParams.searchYear}
                                    onChange={handleInputChange}
                                >
                                    {Array.from({ length: 10 }, (_, i) => {
                                        const year = (currentYear - i).toString()
                                        return (
                                            <option key={year} value={year}>
                                                {year} 년
                                            </option>
                                        );
                                    })}
                                </select>
                                &ensp;
                                <select id='searchMonth'
                                    name='searchMonth'
                                    value={searchParams.searchMonth}
                                    onChange={handleInputChange}
                                >
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const month = (i + 1).toString().padStart(2, '0');
                                        return (
                                            <option key={month} value={month}>
                                                {month} 월
                                            </option>
                                        );
                                    })}
                                </select>
                            </td>
                            <th>기관구분</th>
                            <td colSpan='3'>
                                <select id='searchType'
                                    name='searchType'
                                    value={searchParams.searchType}
                                    onChange={handleInputChange}
                                >
                                    <option value='01'>센터형</option>
                                    <option value='02'>기관형</option>
                                </select>
                                <span className='co_red ml-2'>* 표출되는 항목에 영향을 줍니다.</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className='search_btn_wrapper'>
                <span className='total_items'>전체&nbsp;<strong className={fontColor}>{totalItems}</strong>건</span>
                <div className='btn_container'>
                    <span className='co_red mr-3'>{isSearched ? '※ ' + searchedTime + ' 기준으로 조회 된 데이터 입니다.' : ''}</span>
                    <button onClick={search} disabled={isLoading ? 'disabled' : ''} className={`search_btn ${primaryBackColor}`}><Search16w />검색</button>
                    <FidelityExcelDownload
                        targetRef={tableRef}
                        disabled={!(isSearched && !isLoading)}
                        excelFileName={`충실도_월별전송내역_${excelInfo.emogdesc}_${excelInfo.yyyymm}(${searchParams.searchType === '01' ? '센터형' : '기관형'})`}
                        colWidths={SEND_STAT_COL_WIDTHS}
                    />
                </div>
            </div>
            {isLoading && <Loading />}
            <div ref={tableRef} className={`flex flex-col flex-grow min-h-0 ${isLoading ? 'hidden' : ''}`}>
                <SendStatMonthlyGrid
                    ref={gridRef}
                    searchParams={searchParams}
                    showTitle={false}
                    onSearched={(result) => {
                        setIsSearched(true);
                        setTotalItems(result.totalItems);
                        setSearchedTime(result.searchedTime);
                        setExcelInfo({ emogdesc: result.emogdesc, yyyymm: result.yyyymm });
                    }}
                />
                <SendStatMonthlyDesc />
            </div>
        </Main>
    )
}

export default Send_stat_monthly_2016;
